/**
 * @fileoverview Content editor utilities for flow and node management.
 * Provides helper functions for flow sections, content node updates,
 * and link validation in the editor context.
 */

import _ from "lodash";
import store from "@/store";
import { IStandardFlow } from "../editorType";
import { ContentNode } from "../type";

/**
 * Interface for flow section label objects.
 */
interface FlowLabelObject {
  value: string;
  label: string;
}

/**
 * Generates a list of flow section titles with labels, excluding router flows.
 *
 * @param {Object} flows - Dictionary of flows keyed by flowId
 * @returns {FlowLabelObject[]} Array of section objects with value and label
 *
 * @example
 * const sections = flowSectionsTitle(store.state.floweditor.flows);
 * // Returns: [{ value: 'section1', label: 'Welcome (Main Flow)' }, ...]
 */
export function flowSectionsTitle(flows: { [flowId: string]: IStandardFlow }): FlowLabelObject[] {
  const allFlows = Object.values(flows);
  const temp = _.chain(allFlows)
    .filter(notRouterFlow)
    .map(getFlowSections)
    .flattenDeep()
    .map(getSectionTitle)
    .value() as unknown; // lodash .value returns a boolean[]

  return temp as FlowLabelObject[];
}

const notRouterFlow = ({ title }: { title: string }) => {
  return title.toLowerCase() !== "router";
};
const getFlowSections = (flow: IStandardFlow) => {
  return flow.sections;
};
const getSectionTitle = ({
  sectionId,
  title,
  flowId,
}: {
  sectionId: string;
  title: string;
  flowId: string;
}) => {
  const flow = _.get(store, `state.floweditor.flows[${flowId}]`, {});
  return {
    value: sectionId,
    label: `${title} (${flow.title})`,
  };
};

/**
 * Interface for content node update operations.
 */
export interface ContentNodeObject {
  id: string;
  payload: ContentNode;
  isDelete?: boolean;
  author?: string;
}

/**
 * Updates content nodes based on an array of content node operations.
 * Supports both update and delete operations.
 *
 * @param {Object} originalContentNodes - Original content nodes dictionary
 * @param {ContentNodeObject[]} contentNodeObjects - Array of node update operations
 * @returns {Object} Object containing updatedContentNodes
 *
 * @example
 * const result = updateContentNodeObject(nodes, [
 *   { id: 'node1', payload: updatedNode },
 *   { id: 'node2', isDelete: true }
 * ]);
 */
export function updateContentNodeObject(
  originalContentNodes: { [nodeId: string]: ContentNode },
  contentNodeObjects: ContentNodeObject[]
) {
  let updatedContentNodes = _.cloneDeep(originalContentNodes);
  contentNodeObjects.forEach((contentNodeObject) => {
    const id = _.get(contentNodeObject, "id");
    const contentNode = _.get(contentNodeObject, "payload");
    const isDelete = _.get(contentNodeObject, "isDelete", false);

    let deleted = false;
    let isUpdated = false;

    if (isDelete) {
      delete updatedContentNodes[id];
      deleted = true;
    } else {
      updatedContentNodes = Object.assign({}, updatedContentNodes, {
        [id]: contentNode,
      });
      isUpdated = true;
    }

    // FIXME: console logs to be removed once dev is completed
    console.log("isUpdated: ", isUpdated);
    console.log("deleted: ", deleted);
  });

  return { updatedContentNodes };
}

/**
 * Formats flow section labels by appending '_1' to their values.
 *
 * @template T
 * @param {Array} labelArray - Array of label objects with value and label properties
 * @returns {Array} Array with formatted value properties
 *
 * @example
 * const formatted = formatFlowSection([
 *   { value: 'section1', label: 'Welcome' }
 * ]);
 * // Returns: [{ value: 'section1_1', label: 'Welcome' }]
 */
export function formatFlowSection<T>(
  labelArray: {
    value: string;
    label: T;
  }[]
): {
  value: string;
  label: T & "_1";
}[] {
  let formattedArray: any[] = [];
  if (Array.isArray(labelArray)) {
    formattedArray = labelArray.map((label) => {
      const formattedLabel = Object.assign({}, label, {
        value: `${label.value}_1`,
      });
      return formattedLabel;
    });
  }

  return formattedArray as {
    value: string;
    label: T & "_1";
  }[];
}

/**
 * Checks if a link (content or event) exists in the store.
 *
 * @param {string} next - The link identifier to check
 * @returns {boolean} True if the link exists as either content or event
 *
 * @example
 * if (doesLinkExist(node.next)) {
 *   // Link is valid
 * }
 */
export function doesLinkExist(next: string): boolean {
  const contentExists = doesContentExist(next);
  const eventExists = doesEventExist(next);
  return contentExists || eventExists;
}

/**
 * Checks if content exists in the store by content ID.
 *
 * @param {string} contentId - The content identifier to check
 * @returns {boolean} True if the content exists
 *
 * @example
 * if (doesContentExist('content123')) {
 *   // Content node exists
 * }
 */
export function doesContentExist(contentId: string) {
  const contentExists = !!store.state.nodes.content[contentId];
  return contentExists;
}

/**
 * Checks if an event exists in the store by event ID.
 *
 * @param {string} eventId - The event identifier to check
 * @returns {boolean} True if the event exists
 *
 * @example
 * if (doesEventExist('event123')) {
 *   // Event node exists
 * }
 */
export function doesEventExist(eventId: string) {
  const eventExists = !!store.state.nodes.event.includes(eventId);
  return eventExists;
}

/**
 * Checks if an API mapping exists in the store by mapping ID.
 *
 * @param {string} mappingId - The mapping identifier to check
 * @returns {boolean} True if the API mapping exists
 *
 * @example
 * if (doesApiMappingExist('mapping123')) {
 *   // API mapping exists
 * }
 */
export function doesApiMappingExist(mappingId: string): boolean {
  const apiMappings = store.state.apieditor.mappings;
  const mappingExists = !!apiMappings[mappingId];
  return mappingExists;
}
